import {
  Dialog,
  IconButton,
  useTheme,
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OfficialData, sharePin } from '../../../networkRequest/officialsService';
import { useUser } from '../../../hooks/useUser';
import { useState } from 'react';
import Utils from '../../../utils/utils'
import { z as zod } from 'zod';

interface SharePinDialogProps {
  open: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  official: OfficialData,
  pin: string | null;
  onError: React.Dispatch<React.SetStateAction<string>>;
  onSuccess: React.Dispatch<React.SetStateAction<string>>;
}

const SharePinDialog = ({
  open,
  setDialogOpen,
  official,
  pin,
  onError,
  onSuccess
}: SharePinDialogProps) => {
  const { user } = useUser();
  const theme = useTheme();
  const [emails, setEmails] = useState<string>(official.email ?? "");

  const closeDialog = () => {
    setDialogOpen(false);
  }

  const shareOfficialPin = async () => {
    const emailArray = Utils.parseCommaSeparatedList(emails)
    const emailValidator = zod.string().email();
    const emailSchema = zod.array(emailValidator).nonempty();
    const emailValidationResult = emailSchema.safeParse(emailArray);

    if (emailValidationResult.success) {
      const response = await sharePin(user!.accessToken, official.id, emailValidationResult.data)

      if (response.error) {
        onError(response.error)
      } else {
        setEmails(official.email ?? "")
        onSuccess("Verifier PIN has been shared");
        closeDialog();
      }
    } else {
      const errorCodes = emailValidationResult.error.issues.map(issue => issue.code);
      let errorMessage;
      if (errorCodes.length === 1 && errorCodes[0] === "too_small") {
        errorMessage = `Emails cannot be empty`
      } else {
        const invalidEmails = emailValidationResult.error.issues.map(issue => emailArray[issue.path[0]])
        errorMessage = `Invalid Email: ${invalidEmails.join(', ')}`
      }
      onError(errorMessage)
    }

  }

  if (!pin) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth={false}
        fullWidth
        slotProps={{
          backdrop: { invisible: true },
          paper: {
            style: {
              borderRadius: 8,
              width: '48.9375rem',
            }
          }
        }}
        data-testid={'share-pin-dialog'}
      >
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon sx={{ fontSize: 32, color: theme.palette.grey[600] }} />
        </IconButton>

        <Box
          style={{
            padding: '2rem 1.5rem 1rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            gap: 'var(--3, 1.5rem)',
          }}
        >
          <Typography
            variant="h5"
          >
            Share Verifier PIN
          </Typography>

          <Box
            style={{
              backgroundColor: theme.palette.grey[50],
              width: '100%',
              padding: '0.75rem',
              gap: 1
            }}
          >
            <Typography
              variant="h4"
            >
              {pin}
            </Typography>

            <Typography
              variant="h6"
              style={{
                paddingTop: '0.75rem',
                fontSize: '12px'
              }}
            >
              The agency is responsible for securely sharing and protecting the PIN.
            </Typography>
          </Box>

          <Divider sx={{ width: '100%' }} />

          <Typography
            variant="h6"
            style={{
              fontSize: '20px'
            }}
          >
            Share
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            sx={{ width: "100%" }}
          >
            <TextField
              label="Email"
              placeholder="name@email.com"
              defaultValue={emails || undefined}
              onChange={(e) => setEmails(e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px"
                }
              }}
            />

            <Button
              variant="outlined"
              onClick={shareOfficialPin}
              disabled={!user}
              sx={{
                color: theme.palette.text.primary,
                borderColor: theme.palette.text.primary,
                borderRadius: "10px",
                whiteSpace: "nowrap",
                fontSize: "14px",
                fontWeight: 600,
                paddingLeft: "24px",
                paddingRight: "24px"
              }}
            >
              SHARE PIN
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </>
  )
}

export default SharePinDialog